import React, {createContext, useCallback, useContext, useState} from "react";
import {CheckCircle2, XCircle, X} from "lucide-react";
import {cn} from "./cn";

type ToastTone = "success" | "error";

interface ToastItem {
    id: number;
    message: string;
    tone: ToastTone;
}

interface ToastContextValue {
    showToast: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue>({showToast: () => {}});

export const useToast = () => useContext(ToastContext);

let toastId = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const dismiss = useCallback((id: number) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showToast = useCallback((message: string, tone: ToastTone = "error") => {
        const id = ++toastId;
        setToasts(prev => [...prev, {id, message, tone}]);
        setTimeout(() => dismiss(id), 5000);
    }, [dismiss]);

    return (
        <ToastContext.Provider value={{showToast}}>
            {children}
            <div className="fixed bottom-5 right-5 z-[200] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
                {toasts.map(t => {
                    const Icon = t.tone === "success" ? CheckCircle2 : XCircle;
                    return (
                        <div
                            key={t.id}
                            className={cn(
                                "pointer-events-auto flex items-start gap-3 p-4 rounded-2xl border bg-surface shadow-lg animate-slide-in-right",
                                t.tone === "success" ? "border-success/30" : "border-error/30"
                            )}
                        >
                            <Icon
                                size={18}
                                strokeWidth={2}
                                className={cn("shrink-0 mt-0.5", t.tone === "success" ? "text-success" : "text-error")}
                            />
                            <p className="text-sm text-ink flex-1">{t.message}</p>
                            <button
                                onClick={() => dismiss(t.id)}
                                aria-label="Dismiss"
                                className="shrink-0 text-ink-faint hover:text-ink transition-colors"
                            >
                                <X size={14}/>
                            </button>
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
};
